import React from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import type { CanvasManualAdjustmentModel } from '../../services/api';
import { prettyPrint } from '../../utils/dataUtils';

interface EnrichmentLedgerCardProps {
  ledgerWorkbookId: string;
  isLedgerLoading: boolean;
  isLedgerError: boolean;
  schemaAdjustments: CanvasManualAdjustmentModel[];
}

const EnrichmentLedgerCard: React.FC<EnrichmentLedgerCardProps> = ({
  ledgerWorkbookId,
  isLedgerLoading,
  isLedgerError,
  schemaAdjustments,
}) => (
  <Card variant="outlined">
    <CardContent>
      <Typography variant="h6" gutterBottom>
        Enrichment Ledger
      </Typography>
      {ledgerWorkbookId ? (
        isLedgerLoading ? (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={18} />
            <Typography variant="caption" color="text.secondary">
              Loading ledger entries\u2026
            </Typography>
          </Box>
        ) : isLedgerError ? (
          <Alert severity="warning">
            Unable to load enrichment ledger entries for schema set {ledgerWorkbookId}.
          </Alert>
        ) : schemaAdjustments.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No schema enrichment adjustments recorded yet.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {schemaAdjustments.map((adjustment) => (
              <Paper key={adjustment.adjustment_id} variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2">
                    {adjustment.target_kind === 'schema_table' ? 'Table' : 'Field'}:{' '}
                    <Typography component="span" fontFamily="monospace">
                      {adjustment.target_id}
                    </Typography>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {adjustment.author} \u2022{' '}
                    {new Date(adjustment.created_at).toLocaleString()} \u2022{' '}
                    {adjustment.status}
                  </Typography>
                  {adjustment.rationale && (
                    <Typography variant="body2">{adjustment.rationale}</Typography>
                  )}
                  {Object.keys(adjustment.details ?? {}).length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Details
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5 }}>
                        <pre style={{ margin: 0, fontSize: 11 }}>
                          {prettyPrint(adjustment.details)}
                        </pre>
                      </Paper>
                    </Box>
                  )}
                </Stack>
              </Paper>
            ))}
          </Stack>
        )
      ) : (
        <Typography variant="body2" color="text.secondary">
          Ledger unavailable: assign this draft to a schema set before recording adjustments.
        </Typography>
      )}
    </CardContent>
  </Card>
);

export default EnrichmentLedgerCard;
